import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { customFetch } from '../apiClient';

const DepartmentForm = ({ onSubmitSuccess }) => {
  const [departmentName, setDepartmentName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [departments, setDepartments] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [touched, setTouched] = useState(false);
  
  const formRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoadingList(true);
      const res = await customFetch('/Departments');
      if (res.ok) setDepartments(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (formRef.current) {
      const firstInput = formRef.current.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
      );
      firstInput?.focus();
    }
  }, []); 

  const handleKeyDown = useCallback((e) => {
    const focusableElements = formRef.current?.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, button:not([disabled])'
    );
    if (!focusableElements || focusableElements.length === 0) return;

    const currentIndex = Array.from(focusableElements).indexOf(e.target);

    if (e.key === 'Enter' && currentIndex === focusableElements.length - 1) {
      e.target.closest('form').requestSubmit();
      return;
    }

    if (['Enter', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIndex = Math.min(focusableElements.length - 1, currentIndex + 1);
      focusableElements[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = Math.max(0, currentIndex - 1);
      focusableElements[prevIndex]?.focus();
    }
  }, []);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (formRef.current && e.target.closest('form') === formRef.current) {
        handleKeyDown(e);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleKeyDown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!departmentName.trim()) {
      setError("Please enter department name");
      setTouched(true);
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const res = await customFetch('/Departments', {
        method: "POST",
        body: JSON.stringify({ departmentName: departmentName.trim() }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSuccess("Department added successfully!");
      setDepartmentName("");
      setTouched(false);
      loadDepartments();
      if (onSubmitSuccess) onSubmitSuccess();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (dept) => {
    setEditId(dept.srno);
    setEditData({ departmentName: dept.departmentName || "" });
    setError("");
    setSuccess("");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData({});
    setError("");
    setSuccess("");
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    if (!editData.departmentName.trim()) {
      setError("Department name required");
      return;
    }

    try {
      const payload = {
        srno: editId,
        departmentName: editData.departmentName.trim()
      };

      const res = await customFetch(`/Departments/${editId}`, {
        method: "PUT",
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error(await res.text());

      setDepartments(prev => prev.map(d => 
        d.srno === editId ? { ...d, departmentName: payload.departmentName } : d
      ));
      
      setSuccess("Department updated successfully!");
      cancelEdit();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteDepartment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;

    try {
      const res = await customFetch(`/Departments/${id}`, {
        method: "DELETE"
      });

      if (!res.ok) throw new Error(await res.text());

      setDepartments(prev => prev.filter(d => d.srno !== id));
      setSuccess("Department deleted successfully!");
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Toggle Department Status (Enable/Disable)
  const toggleDepartmentStatus = async (id, currentStatus) => {
    try {
      const res = await customFetch(`/Departments/${id}/toggle-status`, {
        method: "PUT"
      });

      if (!res.ok) {
        if (res.status === 403) {
            throw new Error("You do not have edit permission to change department status.");
        }
        throw new Error(await res.text());
      }

      const data = await res.json();
      
      setDepartments(prev => prev.map(d => 
        d.srno === id ? { ...d, isActive: data.isActive } : d
      ));
      
      setSuccess(`Department ${data.isActive ? 'enabled' : 'disabled'} successfully!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const filteredDepartments = departments.filter(dept =>
    dept.departmentName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="department-form-container">
      <div className="department-card">
        {/* Header */}
        <div className="department-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-building"></i>
            </div>
            <div className="header-text">
              <h5>Department Management</h5>
              <p>Add and manage hospital departments</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <i className="bi bi-list-check"></i>
              <span>{departments.length} Depts</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError('')}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess('')}><i className="bi bi-x"></i></button>
          </div>
        )}

        <div className="department-body">
          {/* Add Form Section */}
          <div className="form-section">
            <div className="section-title">
              <i className="bi bi-plus-circle"></i>
              <span>Add New Department</span>
            </div>
            <div className="section-content">
              <form ref={formRef} onSubmit={handleSubmit} className="add-form">
                <div className="form-group-inline">
                  <div className="input-group-custom">
                    <div className="input-wrapper">
                      <input
                        id="departmentName" type="text"
                        className={`form-control-custom ${touched && !departmentName.trim() ? 'error' : ''} ${touched && departmentName.trim() ? 'success' : ''}`}
                        value={departmentName}
                        onChange={(e) => { setDepartmentName(e.target.value); setTouched(true); }}
                        placeholder="e.g., Cardiology, Neurology..."
                        required disabled={loading}
                      />
                      {touched && departmentName.trim() && <i className="bi bi-check-circle-fill input-icon success-icon"></i>}
                      {touched && !departmentName.trim() && <i className="bi bi-exclamation-circle-fill input-icon error-icon"></i>}
                    </div>
                  </div>
                  <button type="submit" className="btn-add" disabled={loading} tabIndex={1}>
                    {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-plus-lg"></i>}
                    {loading ? ' Saving...' : ' Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Department List Section */}
          <div className="list-section">
            <div className="list-header">
              <div className="list-title">
                <i className="bi bi-list-ul"></i>
                <span>Department List</span>
              </div>
              <div className="search-box">
                <i className="bi bi-search"></i>
                <input
                  type="text"
                  placeholder="Search departments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="table-container">
              {loadingList ? (
                <div className="loading-state">
                  <div className="spinner-custom"></div>
                  <p>Loading...</p>
                </div>
              ) : filteredDepartments.length === 0 ? (
                <div className="empty-state">
                  <i className="bi bi-inbox"></i>
                  <p>{searchQuery ? "No matches found" : "No departments added yet"}</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="departments-table">
                    <thead>
                      <tr>
                        <th className="col-sr">Sr.</th>
                        <th>Department Name</th>
                        <th className="col-status">Status</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDepartments.map((dept, index) => (
                        <tr key={dept.srno} className={`${editId === dept.srno ? 'editing' : ''} ${!dept.isActive ? 'row-inactive' : ''}`}>
                          <td className="col-sr">
                            <span className="sr-badge">{index + 1}</span>
                          </td>
                          {editId === dept.srno ? (
                            <>
                              <td>
                                <input
                                  type="text" className="form-control-edit"
                                  value={editData.departmentName}
                                  onChange={(e) => handleEditChange("departmentName", e.target.value)}
                                  autoFocus
                                />
                              </td>
                              <td className="col-status text-muted">-</td>
                              <td className="col-actions">
                                <div className="action-buttons">
                                  <button className="btn-action btn-save" onClick={saveEdit} disabled={loading} title="Save">
                                    <i className="bi bi-check-lg"></i>
                                  </button>
                                  <button className="btn-action btn-cancel" onClick={cancelEdit} title="Cancel">
                                    <i className="bi bi-x-lg"></i>
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>
                                <div className={`department-name ${!dept.isActive ? 'text-muted' : ''}`}>
                                  <i className="bi bi-building-check"></i>
                                  {dept.departmentName}
                                </div>
                              </td>
                              <td className="col-status">
                                <span className={`status-badge ${dept.isActive ? 'active' : 'inactive'}`}>
                                  {dept.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="col-actions">
                                <div className="action-buttons">
                                  <button 
                                      className={`btn-icon ${dept.isActive ? 'btn-warning' : 'btn-success'}`} 
                                      onClick={() => toggleDepartmentStatus(dept.srno, dept.isActive)} 
                                      title={dept.isActive ? "Disable Department" : "Enable Department"}
                                  >
                                    <i className={dept.isActive ? "bi bi-eye-slash" : "bi bi-eye"}></i>
                                  </button>
                                  <button className="btn-icon btn-edit" onClick={() => startEdit(dept)} title="Edit">
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button className="btn-icon btn-delete" onClick={() => deleteDepartment(dept.srno)} title="Delete">
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .department-form-container {
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .department-card {
          background: white; border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* Header */
        .department-header {
          background: var(--theme-color, #0088cc);
          padding: 8px 12px; display: flex; justify-content: space-between;
          align-items: center; border-bottom: 2px solid #00d4aa; flex-wrap: wrap; gap: 8px;
        }

        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon {
          width: 24px; height: 24px; background: rgba(255, 255, 255, 0.2);
          border-radius: 4px; display: flex; align-items: center; justify-content: center;
          font-size: 13px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .stat-item {
          background: rgba(255, 255, 255, 0.2); padding: 4px 8px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; color: white; font-size: 11px; font-weight: 600;
        }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        .department-body { padding: 12px; }

        /* Add Form */
        .form-section {
          background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; margin-bottom: 12px;
        }
        .section-title {
          background: var(--theme-color, #3182ce); color: white;
          padding: 6px 10px; display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600;
        }
        .section-content { padding: 10px; }

        .form-group-inline { display: flex; gap: 8px; align-items: stretch; }
        .input-group-custom { flex: 1; }
        
        .input-wrapper { position: relative; }
        .form-control-custom {
          width: 100%; padding: 6px 10px 6px 10px; border: 1px solid #cbd5e0; border-radius: 4px;
          font-size: 12px; outline: none; transition: 0.2s; background: white;
        }
        .form-control-custom:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }
        .form-control-custom.error { border-color: #e53e3e; }
        .form-control-custom.success { border-color: #27ae60; }
        
        .input-icon { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); font-size: 12px; }
        .success-icon { color: #27ae60; }
        .error-icon { color: #e53e3e; }

        .btn-add {
          padding: 6px 14px; border-radius: 4px; border: none;
          background: var(--theme-color, #3182ce); color: white;
          font-size: 12px; font-weight: 600; cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 4px;
        }
        .btn-add:hover:not(:disabled) { opacity: 0.9; }

        /* List Section */
        .list-section { border: 1px solid #e2e8f0; border-radius: 4px; overflow: hidden; }
        .list-header {
          background: #edf2f7; padding: 8px 10px; display: flex; justify-content: space-between;
          align-items: center; border-bottom: 1px solid #cbd5e0; gap: 10px; flex-wrap: wrap;
        }
        .list-title { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: #4a5568; }

        .search-box { position: relative; flex: 1; max-width: 250px; }
        .search-box i { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: #a0aec0; font-size: 12px; }
        .search-box input {
          width: 100%; padding: 4px 8px 4px 26px; border: 1px solid #cbd5e0;
          border-radius: 4px; font-size: 11px; outline: none;
        }
        .search-box input:focus { border-color: var(--theme-color, #3182ce); }

        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        
        /* Empty / Loading */
        .loading-state, .empty-state { text-align: center; padding: 30px 10px; color: #718096; font-size: 12px; }
        .spinner-custom {
          width: 20px; height: 20px; border: 2px solid #edf2f7; border-top-color: var(--theme-color, #3182ce);
          border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 8px;
        }
        .empty-state i { font-size: 24px; color: #cbd5e0; display: block; margin-bottom: 4px; }
        .empty-state p { margin: 0; font-style: italic; }

        /* Table */
        .departments-table { width: 100%; border-collapse: collapse; font-size: 11px; text-align: left; }
        .departments-table thead th { padding: 6px 10px; background: #f8fafc; color: #4a5568; font-weight: 600; border-bottom: 1px solid #e2e8f0; white-space: nowrap;}
        .departments-table tbody td { padding: 6px 10px; border-bottom: 1px solid #edf2f7; vertical-align: middle; color: #2d3748; }
        .departments-table tbody tr:hover { background: #f7fafc; }
        .departments-table tbody tr.editing { background: #ebf8ff; }

        .col-sr { width: 40px; text-align: center; }
        .col-actions { width: 100px; text-align: center; }

        .sr-badge { background: var(--theme-color, #3182ce); color: white; padding: 2px 6px; border-radius: 3px; font-weight: 600; font-size: 10px; }
        .department-name { display: flex; align-items: center; gap: 6px; font-weight: 500; transition: color 0.2s; }
        .department-name i { color: var(--theme-color, #3182ce); font-size: 12px; }

        /* Status Features */
        .col-status { width: 80px; text-align: center; }
        .status-badge { 
          padding: 3px 8px; 
          border-radius: 12px; 
          font-size: 10px; 
          font-weight: 600; 
        }
        .status-badge.active { background: #c6f6d5; color: #22543d; }
        .status-badge.inactive { background: #fed7d7; color: #742a2a; }
        .row-inactive { background-color: #fbfbfc !important; }
        .text-muted { color: #a0aec0 !important; }
        .text-muted i { color: #a0aec0 !important; }

        /* Editing Input */
        .form-control-edit {
          width: 100%; padding: 4px 6px; border: 1px solid var(--theme-color, #3182ce);
          border-radius: 3px; font-size: 11px; outline: none;
        }

        /* Action Buttons */
        .action-buttons { display: flex; gap: 4px; justify-content: center; }
        .btn-icon, .btn-action {
          border: none; border-radius: 4px; cursor: pointer; display: flex;
          align-items: center; justify-content: center; transition: 0.2s;
        }
        .btn-icon { width: 22px; height: 22px; font-size: 11px; }
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }
        
        .btn-warning { background: #feebc8; color: #dd6b20; }
        .btn-warning:hover { background: #fbd38d; }
        .btn-success { background: #c6f6d5; color: #2f855a; }
        .btn-success:hover { background: #9ae6b4; }

        .btn-action { padding: 4px 8px; font-size: 11px; }
        .btn-save { background: #c6f6d5; color: #2f855a; }
        .btn-save:hover { background: #9ae6b4; }
        .btn-cancel { background: #edf2f7; color: #4a5568; }
        .btn-cancel:hover { background: #e2e8f0; }

        /* Responsive */
        @media (max-width: 600px) {
          .form-group-inline { flex-direction: column; }
          .btn-add { width: 100%; justify-content: center; }
          .list-header { flex-direction: column; align-items: stretch; }
          .search-box { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default DepartmentForm;